import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Menu,
  MenuItem,
  Stack,
  Divider,
  Tooltip,
  Dialog,
  Checkbox,
  Button,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import { Delete } from "@mui/icons-material";
import { displayColumn, columnCenter, rowSB } from "../../GlobalStyles";
import { useAppDispatch, useAppSelecter } from "../../hooks/storeHooks";
import { RootState } from "../../store/store";
import apiRequest from "../../request/apiRequest/apiRequest";
import { allApis } from "../../request/config";
import { showSnack } from "../../store/slices/constants";
import CustomSearch, { fuzzySearch } from "../../Components/CustomSearch";
import CustomTextField from "../../Components/CustomTextfield";
import { allStatus } from "../../store/slices/constants/staticContents";
import AddSubtopicsDialog from "./AddSubtopicsDialog";
import Actions from "./Actions";
import ConfirmationDialog from "../../Components/ConfirmationDialog";

import GlobalLoader from "../../Components/GlobalLoader";
import { convertFirstUpper } from "../../Regex";
import NodeItem from "./NodeItem";
import {
  bgcolors,
  colors,
  formatNestedList,
  getNextPath,
  Node,
  serachFilterFunction,
  TypeChip,
} from "./Utils";
import FormComp from "./forms/SingleQuestionAdd/FormComp";
import PathOrderManagment from "../../Components/PathOrderManagment";
import { MockLevelTypes } from "../../store/slices/detailedMaster/detailedMasterSlice";
import { updateRefetch } from "../../store/slices/userManagement";
import CustomContainedButton from "../../Components/CustomContainedButton";
import CustomIconButton from "../../Components/CustomIconButton";
import { subtract } from "lodash";
import QuestionDynamicPreviewDrawer from "./QuestionDynamicPreviewDrawer";
import useLastMockMoved from "../../store/slices/constants/staticContents/useLastMockMoved";
type MockMangementsProps = {
  setFilters: (filters: any) => void;
  filters?: any;
  onlyPreview?: boolean;
  selected?: Record<MockLevelTypes, string[]>;
  expandedMap: Record<MockLevelTypes, string | null>;
  setExpandedMap: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  setPagination: React.Dispatch<React.SetStateAction<Record<number, any>>>;
  pagination: Record<MockLevelTypes, number | null>;
  onSubmit?: any;
  onClose?: any;
  hierarchy?: any;
};
export default function MockMangements({
  setFilters,
  filters,
  onlyPreview,
  selected,
  expandedMap,
  setExpandedMap,
  setPagination,
  pagination,
  hierarchy,
  onSubmit,
  onClose,
}: MockMangementsProps) {
  const {lastMock}=useLastMockMoved()
  const { mocks, mock_loading } = useAppSelecter(
    (state: RootState) => state.detailedMaster
  );
  const [questionPreviewOpen, setQuestionPreviewOpen] = useState<
    false | { node: Node; hierarchy: any }
  >(false);
  const [preview, setPreview] = useState<false | "delete">(false);
  const [confirimation, setComfirmation] = useState<{
    open: boolean;
    type: false | "delete" | "status";
    node: any;
    newStatus?: string;
    parent?: string;
    ids?: string[];
  }>({ open: false, type: false, node: {} as Node });
  const [fullscreen, setfullscreen] = useState<any>(false);
  const [filter_by, setFilter] = useState<any>({
    status: {
      series: "all",
      mocktest: "all",
      section: "all",
      question: "all",
    },
    search: {
      series: "",
      mocktest: "",
      section: "",
      question: "",
    },
  });
  // .filter((s)=>filter_by.status[level]===s.status)
  const [onUpdate, setUpdate] = useState<any>(null);
  // const [update, setUpdate] = useState<any>("exam");
  const [treeData, setTreeData] = useState<Node[]>([]);
  const [selectedItems, setSelectedItems] = useState<any>(selected ?? {});
  // expandedMap keeps track of which child is expanded for each parentKey.
  // e.g. { "__ROOT__": "exam2", "exam2": "mocktest1" }

  useEffect(() => {
    setFilters((p: any) => {
      return { ...p, ...expandedMap };
    });
  }, [expandedMap]);

  useEffect(() => {
    if (mocks.series) {
      if (
        JSON.stringify(
          formatNestedList(treeData.map((s) => s.details) ?? [], "series")
        ) !== JSON.stringify(formatNestedList(mocks.series ?? [], "series"))
      ) {
        setTreeData(formatNestedList(mocks.series ?? [], "series"));
      }
    }
  }, [mocks.series, treeData]);

  function updateChildrenByParentId(
    nodes: Node[],
    parentId: string | null,
    newChildren: Node[],
    TYPE: MockLevelTypes
  ): Node[] {
    return parentId
      ? nodes.map((node) => {
          // If the node matches the parentId, replace its children
          if (node.id === parentId && TYPE === (node.type ? node.type : TYPE)) {
            return { ...node, children: newChildren };
          }

          // Otherwise, recurse into children if they exist
          if (node.children && node.children.length > 0 && parentId) {
            return {
              ...node,
              children: updateChildrenByParentId(
                node.children,
                parentId,
                newChildren,
                TYPE
              ),
            };
          }
          // No match, return node as-is
          return node;
        })
      : newChildren;
  }
  useEffect(() => {
    if (mocks.mocktest) {
      if (
        expandedMap.series &&
        JSON.stringify(
          formatNestedList(
            treeData
              .find((s) => s.id === expandedMap.series)
              ?.children?.map((s) => s.details) ?? [],
            "mocktest"
          )
        ) !== JSON.stringify(formatNestedList(mocks.mocktest ?? [], "mocktest"))
      ) {
        setTreeData(
          updateChildrenByParentId(
            treeData,
            expandedMap.series ?? "", // parent node ID
            formatNestedList(mocks.mocktest ?? [], "mocktest"),
            "series"
          )
        );
      }
    }
  }, [mocks.mocktest, treeData]);
  useEffect(() => {
    if (mocks.section) {
      if (
        expandedMap.mocktest &&
        JSON.stringify(
          formatNestedList(
            (treeData.find((s) => s.id === expandedMap.series)?.children ?? [])
              .find((s) => s.id === expandedMap.mocktest)
              ?.children?.map((s) => s.details) ?? [],
            "section"
          )
        ) !== JSON.stringify(formatNestedList(mocks.section ?? [], "section"))
      ) {
        setTreeData((p) => {
          return updateChildrenByParentId(
            treeData,
            expandedMap.mocktest ?? "", // parent node ID
            formatNestedList(mocks.section ?? [], "section"),
            "mocktest"
          );
        });
      }
    }
  }, [mocks.section, treeData]);
  useEffect(() => {
    if (mocks.question) {
      if (
        expandedMap.section &&
        JSON.stringify(
          formatNestedList(
            (treeData.find((s) => s.id === expandedMap.series)?.children ?? [])
              .find((s) => s.id === expandedMap.mocktest)
              ?.children?.find((s) => s.id === expandedMap.section)
              ?.children?.map((s) => s.details) ?? [],
            "question"
          )
        ) !== JSON.stringify(formatNestedList(mocks.question ?? [], "question"))
      ) {
        setTreeData(
          updateChildrenByParentId(
            treeData,
            expandedMap.section ?? "", // parent node ID
            formatNestedList(mocks.question ?? [], "question"),
            "section"
          )
        );
      }
    }
  }, [mocks.question, treeData]);
  // useEffect(() => {
  //   if (masters.topics) {
  //     if (
  //       expandedMap.UNIT &&
  //       JSON.stringify(
  //         formatNestedList(
  //           (treeData.find((s) => s.id === expandedMap.series)?.children ?? [])
  //             .find((s) => s.id === expandedMap.mocktest)
  //             ?.children?.find((s) => s.id === expandedMap.section)
  //             ?.children?.find((s) => s.id === expandedMap.UNIT)
  //             ?.children?.map((s) => s.details) ?? [],
  //           "TOPIC"
  //         )
  //       ) !== JSON.stringify(formatNestedList(masters.topics ?? [], "TOPIC"))
  //     ) {
  //       setTreeData(
  //         updateChildrenByParentId(
  //           treeData,
  //           expandedMap.UNIT ?? "", // parent node ID
  //           formatNestedList(masters.topics ?? [], "TOPIC"),
  //           "UNIT"
  //         )
  //       );
  //     }
  //   }
  // }, [masters.topics]);
  // useEffect(() => {
  //   if (masters.subtopics) {
  //     if (
  //       expandedMap.TOPIC &&
  //       JSON.stringify(
  //         formatNestedList(
  //           (treeData.find((s) => s.id === expandedMap.series)?.children ?? [])
  //             .find((s) => s.id === expandedMap.mocktest)
  //             ?.children?.find((s) => s.id === expandedMap.section)
  //             ?.children?.find((s) => s.id === expandedMap.UNIT)
  //             ?.children?.find((s) => s.id === expandedMap.TOPIC)
  //             ?.children?.map((s) => s.details) ?? [],
  //           "SUBTOPIC"
  //         )
  //       ) !==
  //         JSON.stringify(formatNestedList(masters.subtopics ?? [], "SUBTOPIC"))
  //     ) {
  //       setTreeData(
  //         updateChildrenByParentId(
  //           treeData,
  //           expandedMap.TOPIC ?? "", // parent node ID
  //           formatNestedList(masters.subtopics ?? [], "SUBTOPIC"),
  //           "TOPIC"
  //         )
  //       );
  //     }
  //   }
  // }, [masters.subtopics, treeData]);
  // useEffect(() => {
  //   if (masters.clusters) {
  //     if (
  //       expandedMap.SUBTOPIC &&
  //       JSON.stringify(
  //         formatNestedList(
  //           (treeData.find((s) => s.id === expandedMap.series)?.children ?? [])
  //             .find((s) => s.id === expandedMap.mocktest)
  //             ?.children?.find((s) => s.id === expandedMap.section)
  //             ?.children?.find((s) => s.id === expandedMap.UNIT)
  //             ?.children?.find((s) => s.id === expandedMap.TOPIC)
  //             ?.children?.find((s) => s.id === expandedMap.SUBTOPIC)
  //             ?.children?.map((s) => s.details) ?? [],
  //           "CLUSTER"
  //         )
  //       ) !==
  //         JSON.stringify(formatNestedList(masters.clusters ?? [], "CLUSTER"))
  //     ) {
  //       setTreeData(
  //         updateChildrenByParentId(
  //           treeData,
  //           expandedMap.SUBTOPIC ?? "", // parent node ID
  //           formatNestedList(masters.clusters ?? [], "CLUSTER"),
  //           "SUBTOPIC"
  //         )
  //       );
  //     }
  //   }
  // }, [masters.clusters, treeData]);
  // useEffect(() => {
  //   if (masters.questions.results) {
  //     if (
  //       expandedMap.CLUSTER &&
  //       JSON.stringify(
  //         formatNestedList(
  //           (treeData.find((s) => s.id === expandedMap.series)?.children ?? [])
  //             .find((s) => s.id === expandedMap.mocktest)
  //             ?.children?.find((s) => s.id === expandedMap.section)
  //             ?.children?.find((s) => s.id === expandedMap.UNIT)
  //             ?.children?.find((s) => s.id === expandedMap.TOPIC)
  //             ?.children?.find((s) => s.id === expandedMap.SUBTOPIC)
  //             ?.children?.find((s) => s.id === expandedMap.CLUSTER)
  //             ?.children?.map((s) => s.details) ?? [],
  //           "question"
  //         )
  //       ) !==
  //         JSON.stringify(
  //           formatNestedList(masters.questions?.results ?? [], "question")
  //         )
  //     ) {
  //       setTreeData(
  //         updateChildrenByParentId(
  //           treeData,
  //           expandedMap.CLUSTER ?? "", // parent node ID
  //           formatNestedList(
  //             (masters.questions?.results ?? []) as any,
  //             "question"
  //           ),
  //           "CLUSTER"
  //         )
  //       );
  //     }
  //   }
  // }, [masters.questions.results, treeData]);

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    nodeId: string | null;
  } | null>(null);

  // const handleToggleSelect = (id: string, level: MockLevelTypes, all?: any) => {

  //   setSelectedItems((prev: { [x: string]: string[] }) => {
  //     const allChildsids = all ? all.map((s: { id: any }) => s.id) : [];
  //     const nextKey = getNextPath(level);
  //     return all
  //       ? {
  //           ...prev,
  //           [nextKey]:
  //             prev[nextKey] && prev[nextKey].length
  //               ? allChildsids.every((id: string) => prev[nextKey].includes(id))
  //                 ? [] // if all already selected, deselect all
  //                 : allChildsids // otherwise select all
  //               : allChildsids,
  //         }
  //       : {
  //           ...prev,
  //           [level]:
  //             prev[level] && prev[level].length
  //               ? prev[level].includes(id)
  //                 ? prev[level].filter((x) => x !== id) // toggle off
  //                 : [...prev[level], id] // toggle on
  //               : [id],
  //         };
  //   });
  // };
  const handleToggleSelect = (id: string, level: MockLevelTypes, all?: any) => {
    setSelectedItems((prev: { [x: string]: string[] }) => {
      const nextKey = getNextPath(level);
      const allChildIds = all ? all.map((s: { id: any }) => s.id) : [];

      // Handle "Select All" logic
      if (all) {
        const alreadyAllSelected =
          allChildIds.length > 0 &&
          allChildIds.every((cid: string) => prev[nextKey]?.includes(cid));

        let updated: string[] = prev[nextKey] ? [...prev[nextKey]] : [];

        if (alreadyAllSelected) {
          // Remove only these allChildIds, keep other selections intact
          updated = updated.filter((x) => !allChildIds.includes(x));
        } else {
          // Add missing ones (merge + deduplicate)
          const merged = new Set([...updated, ...allChildIds]);
          updated = Array.from(merged);
        }

        return { ...prev, [nextKey]: updated };
      }

      // Handle individual toggle
      const current = prev[level] || [];
      const updated = current.includes(id)
        ? current.filter((x) => x !== id) // deselect
        : [...current, id]; // select

      return { ...prev, [level]: updated };
    });
  };

  const dispatch = useAppDispatch();
  const [cut, setcut] = useState(false);
  const [loader, setLoader] = useState(false);
  const handleCloseContext = () => setContextMenu(null);
  const handleMove = async () => {
    const selected = selectedItems["question"] ?? [];
    const saveArray =
      mocks.question
        ?.filter((s: any) => selected.includes(s.id))
        .map((s: any) => ({
          id: s.id,
          text: s.text,
        })) ?? [];
    if (expandedMap.series) {
      const series: Node | undefined = treeData.find(
        (s) => s.id === expandedMap.series
      );
      const mocktest: Node | undefined = series?.children?.find(
        (s) => s.id === expandedMap.mocktest
      );
      const section: Node | undefined = mocktest?.children?.find(
        (s) => s.id === expandedMap.section
      );

      if (series && mocktest && section) {
        // 🟢 Decode existing localStorage value safely
        let existing: any[] = [];
        const stored = localStorage.getItem("copied");
        if (stored) {
          try {
            const decoded = atob(decodeURIComponent(escape(stored))); // decode base64 → JSON string
            existing = onSubmit
              ? []
              : JSON.parse(decoded).filter((s: any) => !s.parent);
          } catch (e) {
            console.warn("Invalid base64 or JSON in localStorage:", e);
          }
        }

        // 🟢 Check if same combination already exists
        const existingIndex = existing.findIndex(
          (item: any) =>
            item.series.id === series.id &&
            item.mocktest.id === mocktest.id &&
            item.section.id === section.id
        );

        if (existingIndex !== -1) {
          // Merge new questions with old ones (no duplicates)
          const oldQuestions = existing[existingIndex].questions || [];
          const mergedQuestions = [
            ...oldQuestions.filter(
              (q: any) => !saveArray.some((s) => s.id === q.id)
            ),
            ...saveArray,
          ];
          existing[existingIndex].questions = mergedQuestions;
        } else {
          // Add new entry
          existing.push({
            series,
            mocktest,
            section,
            questions: saveArray,
          });
        }

        // 🟢 Encode before saving
        const encoded = btoa(
          unescape(encodeURIComponent(JSON.stringify(existing)))
        );
        localStorage.setItem("copied", encoded);
        if (onSubmit) {
          onSubmit();
        }
        setTimeout(() => {
          dispatch(updateRefetch(true));
        }, 1);
      } else {
        console.warn("Some nodes not found:", { series, mocktest, section });
      }
    }
  };

  const updateStatusapiCall = async () => {
    setLoader(true);
    const types = confirimation.node.type;
    const statusResponse: any = await apiRequest({
      api:
        types === "question"
          ? allApis.question_cluster_bulk_update
          : allApis.statusUpdate,
      payload: {
        level: types,
        ids: confirimation.node.id
          ? [confirimation.node.id]
          : selectedItems[confirimation.node.type] ?? [],
        status: confirimation.newStatus,
      },
      method: "PUT",
    });
    if (statusResponse && statusResponse.error) {
      setLoader(false);
      dispatch(
        showSnack({
          open: true,
          severity: "error",
          message: statusResponse.msg,
        })
      );
    } else {
      setFilters((p: Record<string, any>) => {
        return { ...p, [`${types}refetch`]: !p[`${types}refetch`] };
      });
      dispatch(
        showSnack({
          open: true,
          severity: "success",
          message: confirimation.node.id
            ? `${types} status updated successfully`
            : `${
                (selectedItems[types] ?? []).length
              } ${types}s status updated successfully`,
        })
      );
      setLoader(false);
      setComfirmation({ open: false, type: false, node: {} as Node });
    }
  };
  const handleStatusSingle = async (
    node: Node | { type: MockLevelTypes },
    status: string
  ) => {
    setComfirmation({
      open: true,
      type: "status",
      node: node,
      newStatus: status,
    });
  };
  const handleDeleteSingle = async (node: any, parent?: any) => {
    setComfirmation({
      open: true,
      type: "delete",
      node: node,
      parent: parent,
    });
  };
  const handleDeleteSingleApi = async () => {
    const data = confirimation.node.type;
    setLoader(true);
    let statusResponse: any;
    if (data === "question") {
      statusResponse = await apiRequest({
        api: allApis.question_cluster_bulk_update,
        payload: {
          level:
            data === "series"
              ? "exam"
              : data === "mocktest"
              ? "mocktest"
              : data === "section"
              ? "section"
              : data === "UNIT"
              ? "unit"
              : data === "TOPIC"
              ? "topic"
              : data === "SUBTOPIC"
              ? "subtopic"
              : data === "question"
              ? "question"
              : "",
          ids: confirimation.node.id
            ? [confirimation.node.id]
            : selectedItems[confirimation.node.type] ?? [],
          delete: true,
        },
        method: "DELETE",
      });
    } else if (data !== "CLUSTER") {
      statusResponse = await apiRequest({
        api: allApis.delete,
        payload: {
          level:
            data === "series"
              ? "exam"
              : data === "mocktest"
              ? "mocktest"
              : data === "section"
              ? "section"
              : data === "UNIT"
              ? "unit"
              : data === "TOPIC"
              ? "topic"
              : data === "SUBTOPIC"
              ? "subtopic"
              : data === "question"
              ? "question"
              : "",
          ids: confirimation.node.id
            ? [confirimation.node.id]
            : selectedItems[confirimation.node.type] ?? [],
        },
        method: "DELETE",
      });
    } else {
      statusResponse = await apiRequest({
        api: allApis.delete_cluster,
        payload: {
          node_id: confirimation.parent ?? "",
          cluster_ids: confirimation.node.id
            ? [confirimation.node.id]
            : selectedItems[data] ?? [],
        },
        method: "DELETE",
      });
    }
    if (statusResponse && statusResponse.error) {
      setLoader(false);
      dispatch(
        showSnack({
          open: true,
          severity: "error",
          message: statusResponse.msg,
        })
      );
    } else {
      setFilters((p: Record<string, any>) => {
        return { ...p, [`${data}refetch`]: !p[`${data}refetch`] };
      });
      setSelectedItems((p: any) => {
        return { ...p, [data]: [] };
      });
      setLoader(false);

      dispatch(
        showSnack({
          open: true,
          severity: "success",
          message: confirimation.node.id
            ? `${convertFirstUpper(data)} deleted successfully`
            : `${
                (selectedItems[data] ?? []).length
              } ${data}s deleted successfully`,
        })
      );
      setComfirmation({ open: false, type: false, node: {} as Node });
    }
  };
  const handleToggleExpand = (
    type: MockLevelTypes,
    nodeId: string,
    parent: string
  ) => {
    setExpandedMap((prev: { [x: string]: any }) => {
      const prevSelected = prev[type];
      const newMap = { ...prev };
      if (type === "series") {
        newMap["mocktest"] = null;
        newMap["section"] = null;
        newMap["question"] = null;
      } else if (type === "mocktest") {
        newMap["section"] = null;
        newMap["question"] = null;
      } else if (type === "section") {
        newMap["question"] = null;
      }
      // If toggling off the currently selected child
      if (prevSelected === nodeId) {
        newMap[type] = null;
        return newMap;
      }
      newMap[type] = nodeId;
      return newMap;
    });
    setPagination((prev) => {
      return {
        ...prev,
        [getNextPath(type)]: 1,
      };
    });
    // setSelectedItems((p: any) => {
    //   return { ...p, [getNextPath(type)]: [] };
    // });
  };
  const getFilteredList = (status: any, level: string, list?: any) => {
    return serachFilterFunction(
      ((list ?? []) as Node[]).filter((s) =>
        status === "all" ? true : status === s.status
      ),
      filter_by.search[level] ?? ""
    ).length;
  };
  const headerDetail = ({
    node,
    parentId,
    onToggleExpand,
  }: {
    node?: Node | null;
    parentId?: string | null;
    onToggleExpand: (
      type: MockLevelTypes,
      nodeId: string,
      parent: string
    ) => void;
  }) => {
    const nodeType = node ? node.type : "series";
    const childType = node
      ? node?.children?.[0]?.type ?? getNextPath(node.type)
      : "series";
    const showExpandIcon = !expandedMap[nodeType];
    const allChild = serachFilterFunction(
      ((node?.children ?? treeData) as Node[]).filter((s) =>
        filter_by.status[childType] === "all"
          ? true
          : filter_by.status[childType] === s.status
      ),
      filter_by.search[childType] ?? ""
    ) as Node[];
    const allChildsids = allChild.map((s: { id: any }) => s.id);
    const allSelected = selectedItems[childType]
      ? allChildsids.every((id: string) =>
          selectedItems[childType].includes(id)
        )
      : false;
    //   (selectedItems[childType] ?? []).length ===
    //  allChild.length;
    const isExpanded = expandedMap[nodeType] === (node?.id ?? "");
    return (
      <Box
        sx={
          node && node.type === "section" && isExpanded
            ? {
                ...rowSB,
                alignItems: "center",
                transition: "all 0.2s ease",
                bgcolor: "background.paper",
                borderRadius: "8px",
                m: "0 5px",
                padding: "5px 10px",
                border: "1px solid #e5e7eb",
              }
            : {
                ...rowSB,
                p: 2,
                alignItems: "center",
                borderBottom: "1px solid #e5e7eb",
                bgcolor: "background.paper",
                borderRadius: "8px",
                m: "0 10px",
                border: "1px solid #e5e7eb",
                transition: "all 0.2s ease",
              }
        }
      >
        <Stack direction="row" alignItems="center" spacing="8px">
          {/* {preview && (
            <ConfirmationDialog
              open={preview === "delete"}
              title={
                preview === "delete"
                  ? "Are you sure you want to delete following content?"
                  : "Selected Content Preview"
              }
              sx={{
                "& .MuiDialog-container": {
                  backdropFilter: "blur(3px)", // subtle blur
                },
                "& .MuiDialog-paper": {
                  margin: "0",
                  minHeight: "100dvh",
                  minWidth: "100%",
                  borderRadius: 0,
                },
              }}
              onClose={() => {
                setPreview(false);
              }}
              onSubmit={() => {
                if (preview === "delete") {
                  handleDeleteSelected(node ? childType : "series", node);
                }
              }}
              content={<Preview selected={selectedItems} />}
            />
          )} */}
          {childType === "question" && (
            <Checkbox
              size="small"
              checked={allSelected}
              // disabled={!showExpandicon?true:isExpanded}
              onClick={(e) => {
                e.stopPropagation();
                if (node) {
                  handleToggleSelect(node.id, node.type, allChild);
                }
              }}
              sx={{ pl: "13px" }}
            />
          )}
          <TypeChip type={childType as any} />

          {nodeType === "series" &&
          (selectedItems["question"] ?? []).length > 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1, minWidth: 80 }}
            >
              {(selectedItems["question"] ?? []).length} questions selected
            </Typography>
          ) : (
            ""
          )}
          <CustomTextField
            autoComplete="off"
            value={
              filter_by.status[childType]
                ? `${filter_by.status[childType]}`
                : ""
            }
            select
            variant="outlined"
            size="small"
            sx={{
              width: "130px",
              "& .MuiInputBase-root": { padding: "7px !important" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderRadius: "4px !important" },
              },
              "& .MuiInputBase-input": {
                textTransform: "capitalize",
                padding: "0 0 0 5px !important",
              },
            }}
            onChange={(e) => {
              setSelectedItems((p: any) => {
                return { ...p, [childType]: [] };
              });
              setFilter((p: any) => {
                return {
                  ...p,
                  status: { ...p.status, [childType]: e.target.value },
                };
              });
            }}
          >
            {allStatus?.map(({ label }: any, i: number) => (
              <MenuItem
                key={i}
                value={label}
                sx={{ textTransform: "capitalize" }}
              >
                {`${label} ${
                  getFilteredList(
                    label,
                    childType,
                    node ? node?.children : treeData
                  ) > 0
                    ? `(${getFilteredList(
                        label,
                        childType,
                        node ? node?.children : treeData
                      )})`
                    : ""
                }`}
              </MenuItem>
            ))}
          </CustomTextField>
          {node?.type === "section" && (
            <CustomTextField
              autoComplete="off"
              value={filters.question_status ? filters.question_status : ""}
              select
              variant="outlined"
              size="small"
              sx={{
                width: "140px",
                "& .MuiInputBase-root": { padding: "7px !important" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderRadius: "4px !important" },
                },
                "& .MuiInputBase-input": {
                  textTransform: "capitalize",
                  padding: "0 0 0 5px !important",
                },
              }}
              onChange={(e) => {
                setFilters((p: any) => {
                  return {
                    ...p,
                    question_status: e.target.value,
                  };
                });
              }}
            >
              {[{ label: "untagged" }, { label: "tagged" }]?.map(
                ({ label }: any, i: number) => (
                  <MenuItem
                    key={i}
                    value={label}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {`${label} ${
                      label === "tagged"
                        ? node.details.total_reviewed_questions
                        : subtract(
                            node.details.total_questions,
                            node.details.total_reviewed_questions
                          )
                    }`}
                  </MenuItem>
                )
              )}
            </CustomTextField>
          )}
          <CustomSearch
            placeholder="Search by name"
            copyData={node ? node.children : treeData}
            fieldName={["name"]}
            value={filter_by.search[childType] ?? ""}
            // filteredData={(array: ObjectValues[]) => {}}
            onClear={() => {}}
            onChange={(val) => {
              setFilter((p: any) => {
                return {
                  ...p,
                  search: { ...p.search, [childType]: val },
                };
              });
            }}
            sx={{
              width: "150px",
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderRadius: "4px !important" },
              },
            }}
          />
        </Stack>

        {/* Right section: Actions */}
        <Box sx={{ display: "flex", gap: "10px" }}>
          {" "}
          {onlyPreview ? (
            ""
          ) : (
            <Stack direction="row" spacing={1.5} alignItems="center">
              {(selectedItems[childType] ?? selectedItems["series"] ?? [])
                .length !== 0 ? (
                <Actions
                  disabled={(selectedItems[childType] ?? []).length === 0}
                  type={node ? childType : "series"}
                  row={
                    filter_by.status[childType] === "all"
                      ? null
                      : { status: filter_by.status[childType] }
                  }
                  onCut={() => {
                    handleMove();
                  }}
                  onDelete={
                    () => {
                      handleDeleteSingle(
                        node
                          ? { type: node ? childType : "series" }
                          : { type: "series" },
                        node?.id ?? ""
                      );
                    }
                    // handleDeleteSelected(node ? childType : "series", node)
                  }
                  onStatus={(status: any) => {
                    handleStatusSingle(
                      { type: node ? childType : "series" },
                      status
                    );
                    // }
                    //   handleStatusUpdate()
                  }}
                />
              ) : (
                ""
              )}
              {/* {getNextPath(node?.type ?? "series") === "question" ||
              getNextPath(node?.type ?? "series") === "CLUSTER" ? (
                ""
              ) : (
                <PathOrderManagment
                  modal={
                    getNextPath(node?.type ?? "series") === "mocktest"
                      ? "mocktest"
                      : "syllabus_tree_node"
                  }
                  list={
                    node?.children ? node?.children.map((s) => s.details) : []
                  }
                  title={
                    convertFirstUpper(
                      getNextPath(node?.type ?? "series").toLowerCase()
                    ) as string
                  }
                  onClear={() => {
                    setFilters((p: Record<string, any>) => {
                      return {
                        ...p,
                        [`${getNextPath(node?.type ?? "series")}refetch`]:
                          !p[`${getNextPath(node?.type ?? "series")}refetch`],
                      };
                    });
                    dispatch(
                      showSnack({
                        open: true,
                        severity: "success",
                        message: `Path order of all the ${convertFirstUpper(
                          getNextPath(node?.type ?? "series").toLowerCase()
                        )}s has been successfully updated.`,
                      })
                    );
                  }}
                />
              )} */}
              {/* <AddSubtopicsDialog
                text={childType}
                onSubmit={() => {
                  setFilters((p: Record<string, any>) => {
                    return {
                      ...p,
                      [`${childType}refetch`]: !p[`${childType}refetch`],
                    };
                  });
                }}
                parent_name={node?.label ?? ""}
                parent={
                  node
                    ? {
                        [`parent_${node.type?.toLowerCase() ?? "exam"}_id`]:
                          node.id,
                      }
                    : {}
                }
              /> */}
            </Stack>
          )}
          {node && node.type === "section" && isExpanded ? (
            ""
          ) : (
            <Tooltip
              children={
                <Box
                  sx={{
                    cursor: "pointer",
                    padding: "1px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => {
                    setfullscreen(
                      fullscreen ===
                        (node
                          ? `parent-${nodeType}-${node.id}`
                          : `parent-${nodeType}`)
                        ? false
                        : node
                        ? `parent-${nodeType}-${node.id}`
                        : `parent-${nodeType}`
                    );
                    if (parentId && node && showExpandIcon) {
                      onToggleExpand(node.type, node.id, parentId);
                    }
                  }}
                >
                  {fullscreen ===
                  (node
                    ? `parent-${nodeType}-${node.id}`
                    : `parent-${nodeType}`) ? (
                    <FullscreenExitIcon />
                  ) : (
                    <FullscreenIcon />
                  )}
                </Box>
              }
              sx={{ zIndex: 9999999 }}
              title={
                fullscreen ===
                (node ? `parent-${nodeType}-${node.id}` : `parent-${nodeType}`)
                  ? "Exit Fullscreen mode"
                  : "Enter Fullscreen mode"
              }
            />
          )}
        </Box>
      </Box>
    );
  };

  const isFullscreen = fullscreen === `parent-series`;
  const pChild = (
    <>
      <Stack display="flex" direction={"row"} width="100%" height="100%">
        <Box
          width={
            questionPreviewOpen !== false &&
            questionPreviewOpen.node.id ===
              questionPreviewOpen.node.details.id &&
            questionPreviewOpen.node.type === questionPreviewOpen.node.type
              ? "auto"
              : "100%"
          }
        >
          {" "}
          {confirimation.open && (
            <ConfirmationDialog
              open={confirimation.open}
              title={"Are you sure you want to"}
              sx={
                {
                  // "& .MuiDialog-container": {
                  //   backdropFilter: "blur(3px)", // subtle blur
                  // },
                  // "& .MuiDialog-paper": {
                  //   margin: "0",
                  //   minHeight: "100dvh",
                  //   minWidth: "100%",
                  //   borderRadius: 0,
                  // },
                }
              }
              onClose={() => {
                setComfirmation({ open: false, type: false, node: {} as Node });
              }}
              onSubmit={() => {
                if (confirimation.type === "status") {
                  updateStatusapiCall();
                } else if (confirimation.type === "delete") {
                  handleDeleteSingleApi();
                }
                {
                }
              }}
              content={
                <>
                  {confirimation.type === "delete"
                    ? `delete ${
                        confirimation.node?.label
                          ? confirimation.node?.label
                          : `${
                              (selectedItems[confirimation.node.type] ?? [])
                                .length
                            } selected`
                      } ${confirimation.node.type}${
                        (selectedItems[confirimation.node.type] ?? []).length >
                        1
                          ? "s"
                          : ""
                      } ?`
                    : `change status of ${
                        confirimation.node?.label
                          ? confirimation.node?.label
                          : `${
                              (selectedItems[confirimation.node.type] ?? [])
                                .length
                            } selected`
                      } ${confirimation.node.type}${
                        (selectedItems[confirimation.node.type] ?? []).length >
                        1
                          ? "s"
                          : ""
                      } to ${convertFirstUpper(confirimation.newStatus ?? "")}`}
                </>
              }
            />
          )}
          {(loader || mock_loading.series) && <GlobalLoader open={true} />}
          <Box
            sx={{
              ...displayColumn,
              height: hierarchy ? "calc(100% - 46px)" : "100%",
            }}
          >
            {expandedMap["series"]
              ? ""
              : headerDetail({ onToggleExpand: handleToggleExpand })}
            <Box
              sx={
                expandedMap["series"]
                  ? {
                      p: 0,
                      height: "100%",
                      ...displayColumn,
                      overflow: "hidden",
                    }
                  : {
                      p: hierarchy ? "2px 10px" : "0 5px",
                      mt: "5px",
                      flex: 8,
                      overflowY: "auto",
                      boxSizing: "border-box",
                    }
              }
              className={expandedMap["series"] ? "" : "scrollBluePrimarythin"}
              id="treeScrollContainer"
            >
              {(
                serachFilterFunction(
                  treeData.filter((s) =>
                    filter_by.status["series"] === "all"
                      ? true
                      : filter_by.status["series"] === s.status
                  ),
                  filter_by.search["series"] ?? ""
                ) as Node[]
              ).filter((s) =>
                onlyPreview
                  ? (selectedItems[s.type] ?? []).includes(s.id)
                  : true
              ).length > 0 ? (
                (
                  serachFilterFunction(
                    treeData.filter((s) =>
                      filter_by.status["series"] === "all"
                        ? true
                        : filter_by.status["series"] === s.status
                    ),
                    filter_by.search["series"] ?? ""
                  ) as Node[]
                )
                  .filter((s) =>
                    onlyPreview
                      ? (selectedItems[s.type] ?? []).includes(s.id)
                      : true
                  )
                  .filter((s) =>
                    expandedMap["series"]
                      ? expandedMap["series"] === (s?.id ?? "")
                      : true
                  )
                  .map((node, index) => (
                    <NodeItem
                    lastMock={lastMock}
                      key={node.id}
                      setPagination={setPagination}
                      setQuestionPreviewOpen={setQuestionPreviewOpen}
                      questionPreviewOpen={questionPreviewOpen}
                      index={index}
                      pagination={pagination}
                      setUpdate={setUpdate}
                      onReFetch={(type: MockLevelTypes) => {
                        setFilters((p: Record<string, any>) => {
                          return {
                            ...p,
                            [`${type}refetch`]: !p[`${type}refetch`],
                          };
                        });
                      }}
                      update={onUpdate}
                      node={node}
                      handleDeleteSingle={handleDeleteSingle}
                      handleStatusSingle={handleStatusSingle}
                      depth={0}
                      hierarchy={{
                        exam: node.label,
                        mocktest: "",
                        section: "",
                        unit: "",
                        topic: "",
                        subtopic: "",
                        cluster: "",
                        question: "",
                        exam_id: node.id,
                        mocktest_id: "",
                        section_id: "",
                        unit_id: "",
                        topic_id: "",
                        subtopic_id: "",
                        cluster_id: "",
                        question_id: "",
                      }}
                      selectAll={false}
                      fullscreen={fullscreen}
                      setfullscreen={setfullscreen}
                      parentId={null}
                      cut={cut}
                      onlyPreview={onlyPreview}
                      getFilteredList={(list: Node[], type: MockLevelTypes) => {
                        return serachFilterFunction(
                          list.filter((s) =>
                            filter_by.status[type] === "all"
                              ? true
                              : filter_by.status[type] === s.status
                          ),
                          filter_by.search[type] ?? ""
                        ) as Node[];
                      }}
                      headerDetail={headerDetail}
                      selectedItems={selectedItems}
                      onToggleSelect={handleToggleSelect}
                      expandedMap={expandedMap}
                      onToggleExpand={handleToggleExpand}
                    />
                  ))
              ) : (
                <Box sx={{ width: "100%", height: "100%", ...columnCenter }}>
                  No exams found
                </Box>
              )}
            </Box>
          </Box>
          {hierarchy && (
            <Box
              sx={{
                p: "5px 10px",
                borderTop: 1,
                borderColor: "divider",
                // bgcolor: "background.paper",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <Box>
                  <CustomIconButton content={"Cancel"} onClick={onClose} />
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <CustomContainedButton
                    content={"Proceed"}
                    otherProps={{
                      onClick: () => {
                        handleMove();
                      },
                    }}
                  />

                  {/*<Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={addNewCluster}
                          >
                            Add New Cluster
                          </Button> */}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        {questionPreviewOpen !== false &&
          questionPreviewOpen.node.id === questionPreviewOpen.node.details.id &&
          questionPreviewOpen.node.type === questionPreviewOpen.node.type && (
            <QuestionDynamicPreviewDrawer
            mt={hierarchy?"0":"63.33px"}
              handleDialogClose={() => {
                setQuestionPreviewOpen(false);
              }}
              open={true}
              title={(() => {
                const hierarchyOrder = [
                  "exam",
                  "goal",
                  "section",
                  "unit",
                  "topic",
                  "subtopic",
                  "cluster",
                ];
                let parts: string[] = [];
                for (const key of hierarchyOrder) {
                  if (questionPreviewOpen.hierarchy[key]) {
                    parts.push(questionPreviewOpen.hierarchy[key]);
                  } else {
                    // Stop at the first missing and add node.label
                    parts.push(questionPreviewOpen.node.label);
                    break;
                  }
                }
                // If all are present, still add node.label at the end
                if (
                  parts.length === hierarchyOrder.length &&
                  parts[parts.length - 1] !== questionPreviewOpen.node.label
                ) {
                  parts.push(questionPreviewOpen.node.label);
                }
                return parts.join(" • ");
              })()}
              selectedRow={{ question: questionPreviewOpen.node.details.id }}
            />
          )}
      </Stack>
    </>
  );

  return isFullscreen ? (
    <Dialog open={true} fullScreen={true} TransitionProps={{ timeout: 400 }}>
      {pChild}
    </Dialog>
  ) : (
    <Paper
      sx={{
        flexGrow: 8,
        m: hierarchy ? "0" : "0 4px 4px",
        ...displayColumn,
        height: hierarchy
          ? "100%"
          : isFullscreen
          ? "100dvh"
          : "calc(100% - 4px)",
        position: isFullscreen ? "fixed" : "relative",
        top: isFullscreen ? 0 : "auto",
        left: isFullscreen ? 0 : "auto",
        zIndex: isFullscreen ? 9999 : "auto",
        minWidth: isFullscreen ? "100%" : "auto",
        transition: "all 0.4s ease",
        background: "transparent",
        boxShadow: "none",
      }}
      id={`parent-series`}
    >
      {onUpdate && onUpdate.node.type === "question" && onUpdate.node.id && (
        <Dialog open fullScreen>
          <FormComp
            onClose={() => setUpdate(false)}
            onSubmit={(que: any) => {
              setUpdate(false);
            }}
            filters={{
              exam: onUpdate.hierarchy.exam,
              mocktest: onUpdate.hierarchy.mocktest,
              section: onUpdate.hierarchy.section,
              unit: onUpdate.hierarchy.unit,
              topic: onUpdate.hierarchy.topic,
              subtopic: onUpdate.hierarchy.subtopic,
              cluster: {
                name: onUpdate.hierarchy.cluster,
                id: onUpdate.parent,
              },
            }}
            question_id={onUpdate.node.id}
          />
        </Dialog>
      )}
      {pChild}
    </Paper>
  );
}
